import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Profile from "./pages/Profile";
import NotFound from "./pages/NotFound";
import Payment from "./pages/Payment";
import PaymentSuccess from "./pages/PaymentSuccess";
import TournamentDetailsPage from "./pages/TournamentDetailsPage";
import LeagueDetailsPage from "./pages/LeagueDetailsPage";
import ChatPage from "./pages/ChatPage";
import RegistrationPage from "./pages/RegistrationPage";
import InvitationPage from "./pages/InvitationPage";
import AllMyEventsPage from "./pages/AllMyEventsPage";
import DeleteAccountPage from "./pages/DeleteAccountPage";
import JudgeAccess from "./pages/JudgeAccess";
import PartnerSearchPage from "./pages/PartnerSearchPage";
import PartnerSearchAllPage from "./pages/PartnerSearchAllPage";
import SplitPaymentPage from "./pages/SplitPaymentPage";
import { usePlatform } from "@/hooks/usePlatform.ts";
import { StatusBar, Style } from "@capacitor/status-bar";
import { useEffect } from "react";
import { App as CapApp } from "@capacitor/app";
import { useForceUpdate} from "@/hooks/useForceUpdate";
import {ForceUpdateScreen} from "@/components/ForceUpdateScreen";
import {useNetworkStatus} from "@/hooks/useNetworkStatus";
import {NoInternetScreen} from "@/components/NoInternetScreen";
import {useNavigate} from "react-router-dom";
import {Browser} from "@capacitor/browser";
import { PUSH_NAV_KEY } from "@/hooks/usePushNotifications";
const queryClient = new QueryClient();

// Deep link listener component (needs to be inside BrowserRouter)
const DeepLinkHandler = () => {
  const navigate = useNavigate();
  const { isNative } = usePlatform();

  // Handle pending push notification navigation
  useEffect(() => {
    const pending = sessionStorage.getItem(PUSH_NAV_KEY);
    if (pending) {
      console.log('[Push] Navigating to pending route:', pending);
      sessionStorage.removeItem(PUSH_NAV_KEY);
      navigate(pending);
    }
  }, [navigate]);

  // Keep checking for push nav while app is open (warm start taps)
  useEffect(() => {
    if (!isNative) return;
    const interval = setInterval(() => {
      const pending = sessionStorage.getItem(PUSH_NAV_KEY);
      if (pending) {
        console.log('[Push] Navigating to pending route:', pending);
        sessionStorage.removeItem(PUSH_NAV_KEY);
        navigate(pending);
      }
    }, 300);
    return () => clearInterval(interval);
  }, [isNative, navigate]);

  useEffect(() => {
    if (!isNative) return;

    const listener = CapApp.addListener('appUrlOpen', async (event) => {
      console.log('[DeepLink] URL received:', event.url);
      // Close in-app browser if open
      try { await Browser.close(); } catch (_) { /* browser may not be open */ }

      // Parse the deep link: racketplaygrid://registration/eventId?session_id=...
      const url = new URL(event.url.replace('racketplaygrid://', 'https://placeholder/'));
      const path = url.pathname;
      const search = url.search;
      console.log('[DeepLink] Navigating to:', path + search);
      navigate(path + search);
    });

    return () => { listener.then(h => h.remove()); };
  }, [isNative, navigate]);

  return null;
};

const App = () => {
  const { isNative, isIOS } = usePlatform();
  const forceUpdate = useForceUpdate();
  const isOnline = useNetworkStatus();

  useEffect(() => {
    if (isNative && isIOS) {
      StatusBar.setOverlaysWebView({ overlay: true });
      StatusBar.setStyle({ style: Style.Dark });
    } else if (isNative) {
      StatusBar.setOverlaysWebView({ overlay: false });
      StatusBar.setStyle({ style: Style.Light });
      StatusBar.setBackgroundColor({ color: '#ffffff' });
    }
  }, [isNative, isIOS]);

  if (!isOnline && isNative) {
    return <NoInternetScreen />;
  }

  if (forceUpdate.required) {
    return <ForceUpdateScreen message={forceUpdate.message} storeUrl={forceUpdate.storeUrl} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <DeepLinkHandler />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile/:userId" element={<Profile />} />
            <Route path="/payment" element={<Payment />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/tournament/:id" element={<TournamentDetailsPage />} />
            <Route path="/league/:id" element={<LeagueDetailsPage />} />
            <Route path="/chat/:id" element={<ChatPage />} />
            <Route path="/registration/:eventId" element={<RegistrationPage />} />
            <Route path="/invitation/:invitationId" element={<InvitationPage />} />
            <Route path="/my-events" element={<AllMyEventsPage />} />
            <Route path="/delete-account" element={<DeleteAccountPage />} />
            <Route path="/judge" element={<JudgeAccess />} />
            <Route path="/partner-search/:id" element={<PartnerSearchPage />} />
            <Route path="/partner-search-all" element={<PartnerSearchAllPage />} />
            <Route path="/split-payment/:splitPaymentId" element={<SplitPaymentPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
